if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      isVerticallyVisible() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        return rect.bottom > 0 && rect.top < viewportHeight;
      }

      getThumbnailScrollContainer() {
        return this.elements.thumbnails?.querySelector('.thumbnail-list') || this.elements.thumbnails || null;
      }

      scrollThumbnailInsideContainer(thumbnail, options = {}) {
        const container = this.getThumbnailScrollContainer();
        if (!container || !thumbnail || typeof container.scrollTo !== 'function') return;

        const behavior = options.behavior || 'auto';
        const align = options.align || 'nearest';
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const isVertical = container.scrollHeight - container.clientHeight > container.scrollWidth - container.clientWidth;
        const isRtl = getComputedStyle(container).direction === 'rtl';
        const scrollOptions = { behavior };

        if (align === 'pinned-second') {
          const visibleChildren = Array.from(container.children || [])
            .map((child) => ({ element: child, rect: child.getBoundingClientRect() }))
            .filter(({ rect }) => {
              if (isVertical) {
                return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
              }

              return rect.right > containerRect.left && rect.left < containerRect.right;
            })
            .sort((first, second) => {
              if (isVertical) {
                return first.rect.top - second.rect.top;
              }

              return isRtl
                ? second.rect.right - first.rect.right
                : first.rect.left - second.rect.left;
            });

          if (visibleChildren.length >= 2) {
            const pinnedRect = visibleChildren[1].rect;

            if (isVertical) {
              const deltaY = thumbnailRect.top - pinnedRect.top;
              if (Math.abs(deltaY) <= 1) return;
              scrollOptions.top = container.scrollTop + deltaY;
            } else {
              const deltaX = isRtl
                ? thumbnailRect.right - pinnedRect.right
                : thumbnailRect.left - pinnedRect.left;
              if (Math.abs(deltaX) <= 1) return;
              scrollOptions.left = container.scrollLeft + deltaX;
            }

            container.scrollTo(scrollOptions);
            return;
          }
        }

        if (isVertical) {
          let deltaY = 0;

          if (align === 'center') {
            deltaY = (thumbnailRect.top + (thumbnailRect.height / 2)) - (containerRect.top + (containerRect.height / 2));
          } else if (thumbnailRect.top < containerRect.top) {
            deltaY = thumbnailRect.top - containerRect.top;
          } else if (thumbnailRect.bottom > containerRect.bottom) {
            deltaY = thumbnailRect.bottom - containerRect.bottom;
          }

          if (Math.abs(deltaY) <= 1) return;
          scrollOptions.top = container.scrollTop + deltaY;
        } else {
          let deltaX = 0;

          if (align === 'center') {
            deltaX = (thumbnailRect.left + (thumbnailRect.width / 2)) - (containerRect.left + (containerRect.width / 2));
          } else if (align === 'start') {
            deltaX = isRtl
              ? thumbnailRect.right - containerRect.right
              : thumbnailRect.left - containerRect.left;
          } else if (align === 'end') {
            deltaX = isRtl
              ? thumbnailRect.left - containerRect.left
              : thumbnailRect.right - containerRect.right;
          } else if (thumbnailRect.left < containerRect.left) {
            deltaX = thumbnailRect.left - containerRect.left;
          } else if (thumbnailRect.right > containerRect.right) {
            deltaX = thumbnailRect.right - containerRect.right;
          }

          if (Math.abs(deltaX) <= 1) return;
          scrollOptions.left = container.scrollLeft + deltaX;
        }

        container.scrollTo(scrollOptions);
      }

      shouldSuppressPageScroll() {
        if (!window.matchMedia('(max-width: 749px)').matches) return false;
        if (this.dataset.disablePageScroll === 'true' || this.dataset.disablePageScrollMobile === 'true') {
          return true;
        }
        return false;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.isVerticallyVisible()) return;
          if (!this.shouldSuppressPageScroll() && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'nearest',
              inline: 'start',
            });
          }
          if (this.shouldSuppressPageScroll()) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.scrollThumbnailInsideContainer(thumbnail, {
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          align: 'pinned-second',
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
